import cn from "classnames";
import { Tooltip } from "react-tooltip";
import { Icon } from "@lynx/ui-components";
import { useMemo } from "react";

import constants from "lib/constants";
import { setColorStatus, getRequiredRefs } from "../helpers";
import { utilityFunctions } from "ui/utilities/utilityFunctions";
import {
  buildMutualExclusivityGraph,
  cabinetMarketKey,
  collectIncludedMarketIdsFromSelections,
  isExclusiveBlockedForFeed,
} from "../market-data-selection";

const MarketDataItem = ({
  title,
  onMarketStatusChange,
  requestedMarketsData,
  changingStatus,
  primaryEntitlements,
  entitlements,
  marketItems,
  marketdataProviders,
}) => {
  const { RequestFormStatusTypes } = constants;

  const includedFeeds = useMemo(
    () =>
      collectIncludedMarketIdsFromSelections(
        entitlements || [],
        marketItems || [],
        cabinetMarketKey
      ),
    [entitlements, marketItems]
  );

  const mutualExclusivityGraph = useMemo(
    () => buildMutualExclusivityGraph(marketItems || []),
    [marketItems]
  );

  const selectedIdsForExclusive = useMemo(() => {
    const s = new Set(entitlements || []);
    includedFeeds.forEach((id) => s.add(id));
    return Array.from(s);
  }, [entitlements, includedFeeds]);
  
  const isIncludedFeed = (marketName) => {
    return includedFeeds.has(marketName);
  };
  
  const renderMarkets = () => {
    return marketItems
      ?.filter((m) => m.group === title)
      .map((item) => {
        const isIncluded = isIncludedFeed(item.name);
        const isChecked = entitlements?.includes(item.name) || isIncluded || false;
        const hardDisabled =
          marketItems.length === 1 ||
          isIncluded ||
          (!changingStatus &&
            requestedMarketsData.status !==
              RequestFormStatusTypes.COMPLETED &&
            requestedMarketsData.status !==
              RequestFormStatusTypes.DECLINED);
        const exclusiveBlocked =
          !hardDisabled &&
          !isChecked &&
          isExclusiveBlockedForFeed(
            item.reference,
            item.name,
            selectedIdsForExclusive,
            marketItems,
            cabinetMarketKey,
            mutualExclusivityGraph
          );
        const isDisabled = hardDisabled;
        
        const onCheckboxHandler = () => {
          if (!isDisabled) {
            onMarketStatusChange(
              item.name,
              !isChecked,
              item.reference,
              item.dependencyRefs
            );
          }
        };

        const requiredRefs = (arrayOfRefsToFind) =>
          getRequiredRefs(arrayOfRefsToFind, marketdataProviders)?.map(
            (name) => <span key={name}>{name}</span>
          );

        return (
          <div
            className={cn("market-item", {
              "added-item":
                !primaryEntitlements?.includes(item.name) &&
                entitlements?.includes(item.name),
              "removed-item":
                primaryEntitlements?.includes(item.name) &&
                !entitlements?.includes(item.name),
              "included-feed": isIncluded,
              "exclusive-blocked": exclusiveBlocked,
              disabled: isDisabled,
            })}
            key={item.name}
          >
            <input
              checked={isChecked}
              type="checkbox"
              name={item.name}
              onChange={onCheckboxHandler}
              disabled={isDisabled}
            />
            <span
              onClick={onCheckboxHandler}
              className={cn("market-checkmark", setColorStatus({
                name: item.name,
                primaryEntitlements,
                entitlements,
                isIncluded,
                isChecked,
              }))}
            ></span>
            <span>{item.name}</span>
            {!!item.dependencyRefs?.length && (
              <span className="market-item-dep-ref-info">
                <Icon id={`anchor-tooltip-${item._id}`} name="info" />
                <Tooltip
                  delayShow={250}
                  delayHide={400}
                  anchorSelect={`#anchor-tooltip-${item._id}`}
                >
                  <div className="i-text">
                    Requires {requiredRefs(item.dependencyRefs)}
                  </div>
                </Tooltip>
              </span>
            )}
            <span className={cn({ "crossed-out": isIncluded })}>
              &nbsp;{utilityFunctions.currencyFormatter().format(item.price)}
            </span>
          </div>
        );
      });
  };

  if (marketItems?.some((m) => m.group === title))
    return (
      <div className="marketdata-item-block">
        <div className="market-title">{title}</div>
        <div className="market-body">{renderMarkets()}</div>
      </div>
    );

  return null;
};

export default MarketDataItem;
